/**
 * Base skeleton utilities and common structures
 */

import {
  RfpBenefitPlan,
  RfpCoverage,
  CoverageTypeEnum,
} from "../../types/rfp.api.types";
import { RfpData } from "../types";

/**
 * Creates an empty benefit plan skeleton
 * All fields are set to empty/null values
 */
export const createEmptyBenefitPlanSkeleton = (): Partial<RfpBenefitPlan> => ({
  identifier: "",
  name: "",
  numberOfLives: undefined,
  type: undefined,
  policyPeriod: undefined,
  fundingType: undefined,
  participationRequirements: undefined,
  rateGuarantee: undefined,
  rateCap: undefined,
  optionName: undefined,
  benefitClassAvailability: [],
  rateSchedule: [],
  benefits: undefined,
});

/**
 * Creates an empty coverage skeleton for a given type
 */
export const createEmptyCoverageSkeleton = (
  type: CoverageTypeEnum,
): Partial<RfpCoverage> => ({
  type,
  status: undefined,
  notes: undefined,
  benefitPlans: [],
});

/**
 * Creates an empty RFP skeleton
 */
export const createEmptyRfpSkeleton = (id: string): RfpData => ({
  id,
  name: "",
  lastModified: new Date().toISOString(),
  coverages: [],
});

/**
 * Deep merges two objects, preferring values from the source object
 *
 * @param target - The skeleton/default object
 * @param source - The data from the backend (preferred)
 * @returns Merged object with backend data taking precedence
 */
export const deepMerge = <T extends object>(
  target: T,
  source: Partial<T>,
): T => {
  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const typedKey = key as Extract<keyof T, string>;
      const sourceValue = source[typedKey];
      const targetValue = result[typedKey];

      // If source value is undefined or null, keep target value
      if (sourceValue === undefined || sourceValue === null) {
        continue;
      }

      // If both are arrays, prefer source array
      if (Array.isArray(sourceValue)) {
        result[typedKey] = sourceValue as T[Extract<keyof T, string>];
      }
      // If both are objects (but not arrays), merge recursively
      else if (
        typeof sourceValue === "object" &&
        typeof targetValue === "object" &&
        targetValue !== null &&
        !Array.isArray(targetValue)
      ) {
        result[typedKey] = deepMerge(targetValue, sourceValue) as T[Extract<
          keyof T,
          string
        >];
      }
      // Otherwise, prefer source value
      else {
        result[typedKey] = sourceValue as T[Extract<keyof T, string>];
      }
    }
  }

  return result;
};
